import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { portalStorage } from "@/services/portalApi";
import { TenantProvider } from "@/contexts/TenantContext";
import { PortalAuthProvider } from "@/contexts/PortalAuthContext";
import { GlossaryTooltipProvider } from "@/contexts/GlossaryTooltipContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { MaintenanceProvider } from "@/providers/MaintenanceProvider";
import { useMaintenanceContext } from "@/contexts/MaintenanceContext";
import { manutencaoEstaTravada } from "@/modules/maintenance/utils/manutencaoTravada";
import { AdminMaintenanceBanner } from "@/modules/maintenance/components/AdminMaintenanceBanner";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { BrandingProvider } from "@/contexts/BrandingProvider";
import { CallProvider } from "@/contexts/CallContext";
import { ThemeProvider } from "next-themes";
import { ThemeLoader } from "@/components/layout/ThemeLoader";
import { TTSAlertMounter } from "@/components/global/TTSAlertMounter";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { LimiteDosAvisos } from "@/components/ui/limite-dos-avisos";
import { limparAvisos } from "@/hooks/use-toast";
import { toast as toastSonner } from "sonner";
import GlobalSearchBar from "@/components/search/GlobalSearchBar";

// Layout components
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import AppMain from "./components/layout/AppMain";
import { PresenceProvider } from "@/contexts/PresenceContext";

// Public pages (not lazy loaded for performance)
import Login from "./pages/Login";
import RecuperarSenha from "./pages/RecuperarSenha";
import ResetPassword from "./pages/ResetPassword";
import ActivateAccount from "./pages/ActivateAccount";
import ForceChangePassword from "./pages/ForceChangePassword";

// DEV-only: harness isolado do <VoiceRecorder> (story F1). Gated por
// import.meta.env.DEV → não entra no bundle de produção (o import dinâmico
// nunca é referenciado quando DEV é false).
const VoiceRecorderDevHarness = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/VoiceRecorderDevHarness"))
  : null;
// DEV-only: harness isolado do <AudioMessageBubble> (story F2). Gated por
// import.meta.env.DEV → não entra no bundle de produção.
const AudioBubbleDevHarness = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/AudioBubbleDevHarness"))
  : null;
import { MaintenanceFullscreen } from "./modules/maintenance/pages/MaintenanceFullscreen";

// Portal pages
import PortalLogin from "./pages/portal/PortalLogin";
import PortalPrimeiroAcesso from "./pages/portal/PortalPrimeiroAcesso";
import PortalTrocarSenha from "./pages/portal/PortalTrocarSenha";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalInicio from "./pages/portal/PortalInicio";
import MeusProcessos from "./pages/portal/MeusProcessos";
import PortalFinanceiro from "./pages/portal/PortalFinanceiro";
import RotaComPermissao from "./components/portal/RotaComPermissao";
import PortalDocumentosEquipe from "./pages/portal/PortalDocumentosEquipe";
import PortalTelasDaEquipe from "./pages/portal/PortalTelasDaEquipe";
import PortalComprovantesPage from "./pages/portal/PortalComprovantesPage";
import AtendimentoPage from "./pages/portal/AtendimentoPage";
import PortalPreferenciasPage from "./pages/portal/PortalPreferenciasPage";
import PortalDocumentLibraryPage from "./pages/portal/PortalDocumentLibraryPage";
import PortalKnowledgeBasePage from "./pages/portal/PortalKnowledgeBasePage";
import PortalRemessaPage from "./pages/portal/PortalRemessaPage";
import PortalLayout from "./components/portal/PortalLayout";
import ProtectedPortalRoute from "./components/portal/ProtectedPortalRoute";
import { usePortalPermissions } from "./hooks/usePortalPermissions";
// M10 (revisão final) — de `lib/portal/`, não mais de `PortalLayout.tsx`:
// aquele módulo é um componente React: importar só um predicado dele fazia
// o Vite acender `react-refresh/only-export-components`.
import { dashboardTemConteudo } from "./lib/portal/dashboardTemConteudo";

// Onboarding modal
// TODO: Reativar quando tours tiverem data-tour attributes nos componentes
// import { OnboardingModal } from "./components/onboarding";

// Lotese public form (lazy loaded — Story 2.1)
const ComprovanteFormPage = lazy(() => import("./pages/lotese/public/ComprovanteFormPage"));

// Primeiro acesso pelo reconhecimento facial — tela PÚBLICA do evento de
// 25/09/2026 (Task 11). Lazy de propósito: ela é a única coisa no sistema que
// importa o Amplify (~65 MB em node_modules, chunk `vendor-liveness` no
// vite.config). Importada de forma estática, esse peso entraria no bundle de
// todo mundo que faz login — para uma tela que a maioria abre uma vez na vida.
const PrimeiroAcesso = lazy(() => import("./pages/PrimeiroAcesso"));

// CRDT collaborative editor demo (lazy loaded, dev-only — Story 1.8.3)
const CrdtDemoPage = lazy(() => import("./pages/dev/CrdtDemoPage"));

// Modular routes

// PWA install banner (A2HS, Story X1.1.3) — auto-gate: só mobile + não instalado
import { InstallBanner } from "@/components/pwa/InstallBanner";
import { AvisoDaUrl } from "@/components/notifications/AvisoDaUrl";
import { ReinscricaoDoPush } from "@/components/notifications/ReinscricaoDoPush";
import { NotificationPrompt } from "@/components/pwa/NotificationPrompt";
import { UpdatePrompt } from "@/components/pwa/UpdatePrompt";
import { OfflineQueueIndicator } from "@/components/pwa/OfflineQueueIndicator";
// App instalado / push do parceiro abrindo "/" (PR4 Task 6) — troca o
// manifest.json ativo dentro de /portal.
import { ManifestDoPortal } from "@/components/pwa/ManifestDoPortal";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Sessão do portal salva no navegador ("lembrar-me" do parceiro). Leitura
 * defensiva: localStorage pode lançar em modo privado de alguns navegadores,
 * e isso não pode derrubar a decisão de rota do app interno.
 */
function temSessaoDoPortal(): boolean {
  try {
    return Boolean(portalStorage.getAccessToken());
  } catch {
    return false;
  }
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const manutencao = useMaintenanceContext();

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  if (!isAuthenticated) {
    // App instalado (manifest do portal) e push do parceiro abrem "/". Sem
    // sessão interna, mas com sessão do portal salva, "/" (e só a raiz
    // exata — nenhuma outra rota interna) manda pro portal em vez do login
    // interno; sem nenhuma das duas, cai no /login de sempre.
    if (location.pathname === "/" && temSessaoDoPortal()) {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Senha temporária (1º acesso): bloqueia TODA a app interna até trocar a senha.
  if (user?.password_change_required) {
    return <Navigate to="/force-change-password" replace />;
  }

  // Manutenção (28/09/2026, contrato Front — docs/superpowers/plans/
  // 2026-09-28-manutencao-no-login.md): usuário JÁ logado, com a manutenção
  // travando o sistema. `podeOperar` vem do GET /maintenance/status (via
  // MaintenanceProvider) e reflete se este usuário tem maintenance:activate.
  // `null` (status ainda não respondeu, ou token inválido) não decide NADA
  // aqui — nunca redireciona em falso por falta de dado.
  const travada = manutencaoEstaTravada(manutencao);

  if (travada && manutencao.podeOperar === false) {
    return <Navigate to="/maintenance" replace />;
  }

  // Achado MÉDIO (revisão de segurança, 28/09/2026): ANTES havia um
  // `return` por caminho (com/sem banner) — dois formatos de Fragment
  // diferentes pro MESMO componente. O Fragment ia de 1 filho (`children`
  // sozinho, índice 0) pra 2 (`AdminMaintenanceBanner` no índice 0,
  // `children` empurrado pro índice 1); o React reconcilia por POSIÇÃO
  // dentro de um Fragment sem key, então via um tipo diferente no índice 0 e
  // remontava a subárvore INTEIRA — perdendo estado de formulário, sidebar,
  // conexões WS de providers filhos etc. toda vez que o banner
  // aparecia/sumia. Um SÓ `return`, `children` sempre na mesma posição;
  // liga/desliga só o que está ANTES dele.
  const mostrarBannerDeAdminOperando = travada && manutencao.podeOperar === true;

  return (
    <>
      {mostrarBannerDeAdminOperando ? <AdminMaintenanceBanner /> : null}
      {children}
    </>
  );
};

/**
 * `/portal/central` — PR 5: "Central" saiu do menu, os Comunicados moraram
 * pro Início. A rota vira só um REDIRECT, preservando `location.search`
 * (`?post=<id>`) — um link de notificação antiga, ou de um e-mail já
 * enviado, ainda aponta pra cá, e perder a querystring transformaria um post
 * específico num 404 mudo. Exportado (não usado fora deste arquivo) pra ser
 * testável isolado — ver `src/__tests__/App.rotasDoInicio.test.tsx`.
 */
export function RedirectCentralParaInicio() {
  const location = useLocation();
  return <Navigate to={`/portal/inicio${location.search}`} replace />;
}

/**
 * `/portal/dashboard` — PR 5: só existe com conteúdo (regra do Augusto, a
 * MESMA de `dashboardTemConteudo` que gateia o item do menu em
 * `PortalLayout.tsx` — uma trava só, não duas que podem discordar). Sem
 * conteúdo, o parceiro cai no Início, que é o pouso normal agora.
 */
export function RotaDoDashboard() {
  const { has } = usePortalPermissions();
  return dashboardTemConteudo(has) ? <PortalDashboard /> : <Navigate to="/portal/inicio" replace />;
}

/**
 * Sub-rotas do portal (dentro de `/portal/*`, já autenticado — vive dentro de
 * `PortalAuthProvider` + `ProtectedPortalRoute` + `PortalLayout`). Extraída
 * como componente nomeado pra ser montável com `MemoryRouter` sem precisar de
 * `<App/>` inteiro (providers pesados, @/routes com 46 arquivos) — ver
 * `src/__tests__/App.portalRoutes.test.tsx`, que cobre `/portal/qualquer-coisa`
 * → Início (o catch-all), `/portal/central?post=` preservando a querystring e
 * `/portal/dashboard` com/sem conteúdo, com `Routes`/`MemoryRouter` DE VERDADE
 * (cada página é stub — o que se prova aqui é a decisão de rota).
 */
export function PortalRoutes() {
  return (
    <Routes>
      <Route path="/inicio" element={<PortalInicio />} />
      <Route path="/dashboard" element={<RotaDoDashboard />} />
      <Route path="/comprovantes" element={<PortalComprovantesPage />} />
      <Route path="/processos" element={<MeusProcessos />} />
      <Route path="/central" element={<RedirectCentralParaInicio />} />
      <Route path="/documentos" element={<PortalDocumentLibraryPage />} />
      <Route path="/conhecimento" element={<PortalKnowledgeBasePage />} />
      <Route path="/remessas" element={<PortalRemessaPage />} />
      <Route
        path="/documentos-equipe"
        element={
          <RotaComPermissao permissao="portal:documentos_equipe:view">
            <PortalDocumentosEquipe />
          </RotaComPermissao>
        }
      />
      <Route
        path="/telas-equipe"
        element={
          <RotaComPermissao permissao="portal:equipe:manage">
            <PortalTelasDaEquipe />
          </RotaComPermissao>
        }
      />
      <Route path="/atendimento" element={<AtendimentoPage />} />
      <Route
        path="/financeiro"
        element={
          <RotaComPermissao permissao="portal:financeiro:view">
            <PortalFinanceiro />
          </RotaComPermissao>
        }
      />
      <Route path="/preferencias" element={<PortalPreferenciasPage />} />
      <Route path="/" element={<Navigate to="/portal/inicio" replace />} />
      {/* Qualquer sub-rota desconhecida do portal também pousa no Início —
          antes desta linha não havia catch-all aqui, e
          `/portal/qualquer-coisa` renderizava em branco. */}
      <Route path="*" element={<Navigate to="/portal/inicio" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        storageKey="seweb-theme"
        enableSystem={false}
        disableTransitionOnChange={false}
      >
        <AuthProvider>
          <TenantProvider>
            <OnboardingProvider>
              <GlossaryTooltipProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <MaintenanceProvider>
                    {/* TODO: Reativar quando tours tiverem data-tour attributes nos componentes */}
                    {/* <OnboardingModal /> */}
                    {/* PR4 Task 6: fora de <Routes> (não precisa remontar a cada
                        navegação) mas dentro do Router (usa useLocation). Troca o
                        manifest PWA ativo — SE_WEB interno vs Portal do Parceiro —
                        conforme a rota atual. */}
                    <ManifestDoPortal />
                    <Routes>
                      {/* Public routes */}
                      <Route path="/maintenance" element={<MaintenanceFullscreen />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/recuperar-senha" element={<RecuperarSenha />} />
                      <Route path="/resetar-senha" element={<ResetPassword />} />
                      <Route path="/activate-account" element={<ActivateAccount />} />
                      {/* Troca obrigatória de senha (1º acesso com senha temporária).
                          Top-level (sem sidebar) + a própria tela exige auth e o flag. */}
                      <Route path="/force-change-password" element={<ForceChangePassword />} />

                      {/* DEV-only: preview isolado do gravador de áudio (F1). */}
                      {import.meta.env.DEV && VoiceRecorderDevHarness && (
                        <Route
                          path="/dev/voice-recorder"
                          element={
                            <Suspense fallback={null}>
                              <VoiceRecorderDevHarness />
                            </Suspense>
                          }
                        />
                      )}

                      {/* DEV-only: preview isolado do player de áudio (F2). */}
                      {import.meta.env.DEV && AudioBubbleDevHarness && (
                        <Route
                          path="/dev/audio-bubble"
                          element={
                            <Suspense fallback={null}>
                              <AudioBubbleDevHarness />
                            </Suspense>
                          }
                        />
                      )}

                      {/* Primeiro acesso pelo rosto (evento 25/09/2026).
                          FORA do Layout autenticado de propósito: não há barra
                          lateral, não há sessão, e quem chega aqui ainda não
                          entrou. É a única tela do sistema assim. */}
                      <Route
                        path="/primeiro-acesso"
                        element={
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center h-screen">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                              </div>
                            }
                          >
                            <PrimeiroAcesso />
                          </Suspense>
                        }
                      />

                      {/* Story 2.1 Lotese: Public Comprovante Form */}
                      <Route
                        path="/public/lotese/comprovante"
                        element={
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center h-screen">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                              </div>
                            }
                          >
                            <ComprovanteFormPage />
                          </Suspense>
                        }
                      />

                      {/* Story 1.8.3: CRDT collaborative editor demo — dev-only route.
                          Gated at registration time (NOT only via in-component Navigate) so
                          the lazy chunk + yjs/y-websocket transitive graph are NOT shipped
                          to production users. The component-side `Navigate` ainda existe como
                          defesa em profundidade. */}
                      {import.meta.env.PROD ? null : (
                        <Route
                          path="/dev/crdt-demo"
                          element={
                            <Suspense
                              fallback={
                                <div className="flex items-center justify-center h-screen">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                                </div>
                              }
                            >
                              <CrdtDemoPage />
                            </Suspense>
                          }
                        />
                      )}

                      {/* Story 11.1: Portal de Acesso Externo - Public Route */}
                      <Route
                        path="/portal/login"
                        element={
                          <PortalAuthProvider>
                            <PortalLogin />
                          </PortalAuthProvider>
                        }
                      />

                      {/* Portal — Primeiro Acesso (ativação do convite). PÚBLICA:
                          fica FORA do ProtectedPortalRoute, senão o guard
                          redireciona pro login e o token de ativação se perde. */}
                      <Route
                        path="/portal/primeiro-acesso"
                        element={
                          <PortalAuthProvider>
                            <PortalPrimeiroAcesso />
                          </PortalAuthProvider>
                        }
                      />

                      {/* Troca obrigatória de senha do portal (1º acesso com senha
                          temporária). Em PortalAuthProvider mas FORA do
                          ProtectedPortalRoute pra evitar loop; a tela guarda auth+flag. */}
                      <Route
                        path="/portal/trocar-senha"
                        element={
                          <PortalAuthProvider>
                            <PortalTrocarSenha />
                          </PortalAuthProvider>
                        }
                      />

                      {/* Story 11.1: Portal de Acesso Externo - Protected Routes */}
                      <Route
                        path="/portal/*"
                        element={
                          <PortalAuthProvider>
                            <ProtectedPortalRoute>
                              <PortalLayout>
                                <PortalRoutes />
                              </PortalLayout>
                            </ProtectedPortalRoute>
                          </PortalAuthProvider>
                        }
                      />

                      {/* Protected internal routes */}
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <NotificationProvider>
                              <BrandingProvider>
                                <CallProvider>
                                  {/* Story E6.3.2: Provider de presence — 1 WS pra toda
                                      sessão autenticada. Precisa estar ABAIXO de
                                      ProtectedRoute (useAuth confiável) e ABAIXO de
                                      MaintenanceProvider (caso queira filtrar dot durante
                                      manutenção no futuro). */}
                                  <PresenceProvider>
                                    <ThemeLoader />
                                    <TTSAlertMounter />
                                    <InstallBanner />
                                    <NotificationPrompt />
                                    <AvisoDaUrl />
                                    <ReinscricaoDoPush />
                                    <UpdatePrompt />
                                    <OfflineQueueIndicator />
                                    {/* Story E3.2.1: palette de busca global
                                        (Cmd+K). Global, autenticado, dentro do
                                        Router (useNavigate) e do QueryClient. */}
                                    <GlobalSearchBar />
                                    <div className="flex h-full app-canvas overflow-hidden">
                                      <Sidebar />
                                      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                        <Header />
                                        {/* Redesign mensagens: o <main> é full-bleed (sem
                                            padding + overflow-hidden) SÓ nas rotas de chat/
                                            atendimentos (que rolam por dentro) — elimina a
                                            barra de rolagem dupla. Demais rotas inalteradas. */}
                                        <AppMain />
                                      </div>
                                    </div>
                                  </PresenceProvider>
                                </CallProvider>
                              </BrandingProvider>
                            </NotificationProvider>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                    {/* Avisos globais: DOIS Toasters convivem, cada um com o seu limite de
                        erro (um aviso que quebra o render nunca derruba o resto do App).
                        - shadcn (E20.2.3, 2026-05-23): `@/hooks/use-toast`; mount global
                          dentro do MaintenanceProvider, toda rota autenticada herda o
                          viewport (E20.2.3 precisa do toast "Modo offline" visível quando a
                          WS Yjs cai). TOAST_LIMIT=1 evita pilha. Traz o aviso do sino (D1).
                        - sonner (passo 9, 2026-10): o app chama `toast.*` de "sonner" em 131
                          arquivos, mas o Toaster do sonner nunca foi montado e todo aviso
                          sumia. Montado aqui; o `toast` dele é protegido contra o `detail` do
                          422 (lista de objetos) em `ui/sonner.tsx`. Os dois ficam: trocar um
                          pelo outro mexeria em 131 arquivos. O do sonner fica em z-99, abaixo
                          do viewport do shadcn (z-100), então o aviso do sino fica por cima. */}
                    <LimiteDosAvisos aoFalhar={limparAvisos}>
                      <Toaster />
                    </LimiteDosAvisos>
                    <LimiteDosAvisos aoFalhar={() => toastSonner.dismiss()}>
                      <SonnerToaster />
                    </LimiteDosAvisos>
                  </MaintenanceProvider>
                </Router>
              </GlossaryTooltipProvider>
            </OnboardingProvider>
          </TenantProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
